import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Loader from "../../UI/Loader";
import { getEmbeddableToken } from "../../api/embeddable";
import { useAppSelector } from "../../store/hooks";

const NoResultText = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.33;
  letter-spacing: 0.96px;
  text-align: center;
  color: #061c2e;
  padding: 1rem;
`;

interface AnalyticsProps {
  embeddableId: string;
  pack?: string;
}

// Utility to detect environment
const detectEnvironment = (): string => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'local';
  if (hostname.includes('dev') || hostname.includes('staging')) return 'dev';
  return 'default';
};

const Analytics: React.FC<AnalyticsProps> = ({ embeddableId, pack }) => {
  const { user } = useAppSelector((state) => state.user);
  const analyticsModuleEnabled = user?.role === 'recruiter'
    ? user?.company?.enable_analytics
    : user?.company?.analytics_module_enabled;
  const loader = useRef<HTMLDivElement>(null);
  const embeddableRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [environment] = useState<string>(() => detectEnvironment());
  const [t] = useTranslation();

  console.log('[Analytics.tsx] Props:', { embeddableId, pack });
  console.log('[Analytics.tsx] State:', { loading, token, error, environment });

  // Use the pack as the theme key, fallback to 'hireport'
  const themeKey = pack || 'hireport';

  // Build clientContext from user, company, and theme key
  const clientContext = React.useMemo(() => {
    return {
      companyId: user?.company?.id,
      // company: user?.company?.name || undefined,
      // userEmail: user?.email || undefined,
      // locale: i18n.language,
      theme: themeKey,
      // role: user?.role || undefined,
    };
  }, [user, themeKey]);

  const fetchToken = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[Analytics.tsx] Fetching token with params:', { embeddableId, environment });
      const response = await getEmbeddableToken(embeddableId, environment);
      setToken(response.data.token);
      setLoading(false);
      console.log('[Analytics.tsx] Token fetched successfully:', response.data.token);
    } catch (err: any) {
      console.error('[Analytics.tsx] Error fetching token:', err);
      console.error('[Analytics.tsx] Error response:', err?.response?.data);
      console.error('[Analytics.tsx] Error status:', err?.response?.status);
      console.error('[Analytics.tsx] Error config:', { embeddableId, environment });

      // More specific error messages
      let errorMessage = t("NO_RESULT");
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setLoading(false);
    }
  }, [embeddableId, environment, t]);

  // When environment changes, reload the token
  useEffect(() => {
    localStorage.setItem('embeddable_environment', environment);
    fetchToken();
  }, [environment, fetchToken]);

  // Inject the Embeddable script only once
  useEffect(() => {
    if (!document.getElementById('embeddable-script')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://api.eu.embeddable.com/js/v1/';
      script.id = 'embeddable-script';
      document.body.appendChild(script);
    }
  }, []);

  // Render the web component directly for React 17 compatibility
  useEffect(() => {
    if (token && embeddableRef.current) {
      embeddableRef.current.innerHTML = '';
      const el = document.createElement('em-beddable');
      el.setAttribute('token', token);
      el.setAttribute('client-context', JSON.stringify(clientContext));
      embeddableRef.current.appendChild(el);
      console.log('[Analytics.tsx] em-beddable component rendered with token:', token, 'and context:', clientContext);
    } else {
      console.log('[Analytics.tsx] em-beddable NOT rendered. Token:', token, 'Ref:', embeddableRef.current);
    }
  }, [token, clientContext]);

  if (!analyticsModuleEnabled) {
    console.log('[Analytics.tsx] Analytics module not enabled for user.');
    return null;
  }

  return (
    <>
      <Loader spinning={loading}>
        {error ? (
          <NoResultText>{error}</NoResultText>
        ) : token ? (
          <div style={{ height: "calc(100vh - 64px - 3rem - 40px)" }}>
            <div ref={embeddableRef} style={{ height: '100%' }} />
          </div>
        ) : null}
        <div className="loader" ref={loader} />
      </Loader>
    </>
  );
};

export default Analytics;
